import {store} from '@/store';
import {userAtom} from '@/store/user';
import {buildMbApiUrl, fetchWithTimeout} from '@/util/request-utils';
import {type Job, type JobsResponse} from '@/types/job-constants';

export interface GetJobsParams {
    page?: number;
    per_page?: number;
    state?: 'pending' | 'processing' | 'completed' | 'failed';
    media_kind?: string;
}

export async function getJobs(
    params: GetJobsParams = {}
): Promise<JobsResponse> {
    const {page = 1, per_page = 20} = params;

    try {
        const user = store.get(userAtom);
        if (!user?.auth_token) {
            return {jobs: [], total: 0, page: 1, per_page: 20};
        }

        const queryParams: Record<string, string | number> = {
            page,
            per_page,
        };

        if (params.state) queryParams.state = params.state;
        if (params.media_kind) queryParams.media_kind = params.media_kind;

        const url = buildMbApiUrl(
            '/jobs.json',
            {auth_token: user.auth_token},
            queryParams
        );

        type RawJob = Record<string, unknown>;

        interface JobsApiResponse {
            results: RawJob[];
            total: number;
            page: number;
            per_page: number;
        }

        const fetchResponse = await fetchWithTimeout(url);
        if (!fetchResponse.ok) {
            return {jobs: [], total: 0, page: 1, per_page: 20};
        }
        const response: JobsApiResponse = await fetchResponse.json();

        const rawJobs = response.results ?? [];
        const total = response.total ?? rawJobs.length;

        // Normalize job data - API sometimes returns malformed fields
        // Note: API returns partial job data, we cast to Job[] for compatibility
        const jobs = rawJobs.map((job) => {
            // Normalize status field - API may return 'state' instead of 'status'
            // and sometimes as an object {renders: "completed"} instead of a string
            let status: unknown = job.status ?? job.state;
            if (typeof status === 'object' && status !== null) {
                // Handle case where status is an object like {renders: "completed"}
                const statusObj = status as Record<string, unknown>;
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string should use fallback
                status = (statusObj.renders || statusObj.state) ?? 'pending';
            }
            if (typeof status !== 'string') {
                status = 'pending';
            }

            // Defensive: ensure all string fields are actually strings to prevent render crashes
            const safeString = (
                value: unknown,
                fallback: string = ''
            ): string => {
                if (typeof value === 'string') return value;
                if (typeof value === 'object' && value !== null) {
                    // Try to extract a meaningful string from object
                    const obj = value as Record<string, unknown>;

                    return String(
                        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string should use fallback
                        obj.renders || obj.state || obj.value || fallback
                    );
                }
                return value != null ? String(value) : fallback;
            };

            // Defensive: ensure numeric fields are actually numbers
            const safeNumber = (
                value: unknown,
                fallback: number = 0
            ): number => {
                if (typeof value === 'number' && !isNaN(value)) return value;
                if (typeof value === 'string') {
                    const parsed = Number(value);
                    if (!isNaN(parsed)) return parsed;
                }
                return fallback;
            };

            return {
                ...job,
                // Ensure critical fields that get rendered are properly typed
                id: safeString(job.id, String(Date.now())),
                asset_id: safeString(job.asset_id),
                asset_name: safeString(job.asset_name, 'Unknown'),
                media_kind: safeString(job.media_kind, ''),
                status: status as string,
                supplier_id: safeNumber(job.supplier_id, 0),
                // Ensure dates exist and are valid strings
                created_at: safeString(
                    job.created_at,
                    new Date().toISOString()
                ),
                updated_at: safeString(
                    job.updated_at ?? job.created_at,
                    new Date().toISOString()
                ),
            } as Job;
        });

        return {
            jobs,
            total,
            page,
            per_page,
        };
    } catch {
        return {jobs: [], total: 0, page: 1, per_page: 20};
    }
}
